/**
 * **ショットと投影**（Issue #522 → #523。設計は `docs/fx-camera-design.md`）。
 *
 * 盤は平面（`fields/`）の上にあり、それを**カメラで見る**。
 *
 * ## ショット ＝ three のカメラの数値そのもの（Issue #523。ユーザー判断 2026-09-24）
 *
 * 舞台は three で描く（`stage/three/runtime.js`）ので、**ショットが持つのは
 * `THREE.PerspectiveCamera` が持てる数値だけ**にしてある:
 *
 * | ショット | three |
 * |---|---|
 * | `x` / `y` / `z` | `camera.position` |
 * | `tx` / `ty` / `tz` | `camera.lookAt()` の注視点 |
 * | `up` | `camera.up` |
 * | `fov` | `camera.fov`（**縦**の画角。degree） |
 * | `near` / `far` | `camera.near` / `camera.far` |
 *
 * 横の画角は `camera.aspect`（= 舞台の 幅 / 高さ）から決まるので、ショットは持たない。
 *
 * **`roll` と主点のずらし（`px` / `py`）は廃止した**（Issue #523）。three のカメラに `roll` は無く
 * （傾けたいなら `up` を回す＝`rotated` プリセットがそれ）、主点のずらしは `setViewOffset` で
 * できるものの**標準の見え方ではない**。以前は「完成動画と同じ台形」を厳密に作るために使っていたが、
 * そこまで再現しなくてよい（ユーザー判断）ので、**プリセットは注視点と距離だけで取り直した**。
 *
 * ## 投影の式がここに残っている理由
 *
 * 絵は three が描くが、**HUD と編集の層は舞台のピクセルを知りたい**（枠を盤の辺に沿わせる・
 * ゾーンの枠をドラッグする）。そのための純関数が `project` / `unproject` で、
 * **three のカメラと同じ数値から同じ式**（lookAt ＋ 透視投影）で出す。
 *
 * ## 平面 → ワールド
 *
 * 平面は y = 0 に置き、`(u, v)` を `(u - w/2, 0, v - h/2)` に写す。
 * **v が小さいほど奥＝ P2 の側**（画面の上）で、大きいほど手前＝ P1 の側。
 * 実写も仮想フィールドも同じ向きなので、同じカメラで重ねて見比べられる。
 */

const DEFAULT_CANVAS = Object.freeze({ w: 1920, h: 1080 });

/** three のカメラの既定（ショットが持たなければこれ） */
export const NEAR = 1;
export const FAR = 20000;

/*
 * プリセットは**盤（1080×1260 の平面）が上下左右 40px の余白を残して舞台に収まる**ように
 * 数値で解いてある（`camera.test.js` が見る）。縦いっぱいに使いつつ縦の中央へ寄せるため、
 * 注視点は盤の中心より少し手前（`tz` > 0）を見る —— 遠近が付くと盤の絵の中心は
 * 盤の中心より下に来るため。
 */

/** 実写向け（既定）: P1 の側の上から仰角 70°。**手前が広い台形**（幅比 1.27） */
export const LIVE_SHOT = Object.freeze({
  x: 0, y: 1477.1968, z: 537.6557,
  tx: 0, ty: 0, tz: 76,
  fov: 45,
  up: Object.freeze([0, 1, 0]),
});

/** 真上から。カメラの上を -z（P2 の側）にして、**P1 が下・P2 が上** */
export const TOPDOWN_SHOT = Object.freeze({
  x: 0, y: 1642.631, z: 0,
  tx: 0, ty: 0, tz: 0,
  fov: 45,
  up: Object.freeze([0, 0, -1]),
});

/**
 * **真上から 90° 倒して見る**（`topdown` の `up` を光軸まわりに 90° 回したもの）。
 *
 * 完成動画（AviUtl / Premiere）は合成フィールド動画を 90° 倒して**マット 2 枚を左右に並べる**
 * （`元動画` ネストは 1260×1080）。平面そのものは回さない決まり（README の守ること 9）なので、
 * **回すのはカメラ**＝`up` を `-x` にする。これで盤の座標は 1 つのまま、完成動画と同じ向きで見られる。
 *
 * 向きは完成動画と一致する（`fields/liveParity.test.js` の `asFx` と同じ）: **P1 が左・P2 が右**で、
 * 平面の u が増える向き（実写の動画の右）が画面の下。
 */
export const ROTATED_SHOT = Object.freeze({
  x: 0, y: 1407.9693, z: 0,
  tx: 0, ty: 0, tz: 0,
  fov: 45,
  up: Object.freeze([-1, 0, 0]),
});

/**
 * **90° 回転して、x の側に少し傾けて見る**（ユーザー判断 2026-09-25 で**既定**）。
 *
 * `rotated`（真上から・上が -x ＝ P1 が左・P2 が右）のカメラを、盤の +x の側（画面の下の側）へ 20° 倒した。
 * 手前（画面の下）が少し広い台形になる。距離と注視点は盤が上下 40px の余白で舞台に収まるように解いてある
 * （`camera.test.js` の「盤が舞台に収まる」）。
 */
export const ROTATED_TILT_SHOT = Object.freeze({
  x: 519.2808, y: 1219.2777, z: 0,
  tx: 75.5, ty: 0, tz: 0,
  fov: 45,
  up: Object.freeze([-1, 0, 0]),
});

/** 斜め: 仰角 50°。遠近を強めに（幅比 1.71） */
export const TILT_SHOT = Object.freeze({
  x: 0, y: 1046.4167, z: 878.0479,
  tx: 0, ty: 0, tz: 164,
  fov: 45,
  up: Object.freeze([0, 1, 0]),
});

/** プリセット（名前 → ショット） */
export const SHOTS = Object.freeze({
  live: LIVE_SHOT, topdown: TOPDOWN_SHOT, rotated: ROTATED_SHOT, rotatedTilt: ROTATED_TILT_SHOT, tilt: TILT_SHOT,
});

/** **既定のショット**（ページが最初に使う。演出が舞台を `reset` したときもこれに戻る） */
export const DEFAULT_SHOT_NAME = 'rotatedTilt';

export const SHOT_LABELS = Object.freeze({
  live: '実写', topdown: '真上', rotated: '90° 回転', rotatedTilt: '90° 回転・斜め', tilt: '斜め',
});

/** 設定パネルが出す項目（**この表が一次情報**。足したらパネルに勝手に出る） */
export const CAMERA_FIELDS = Object.freeze([
  { key: 'x', label: 'x（左右）', min: -3000, max: 3000, step: 1 },
  { key: 'y', label: 'y（高さ）', min: 0, max: 4000, step: 1 },
  { key: 'z', label: 'z（前後）', min: -3000, max: 3000, step: 1 },
  { key: 'tx', label: '注視点 x', min: -2000, max: 2000, step: 1 },
  { key: 'ty', label: '注視点 y', min: -1000, max: 1000, step: 1 },
  { key: 'tz', label: '注視点 z', min: -2000, max: 2000, step: 1 },
  { key: 'fov', label: '画角 fov', min: 5, max: 120, step: 0.5 },
]);

/** ショットの長さを k 倍する（画角は長さではないのでそのまま） */
export const scaleShot = (shot, k = 1) => (k === 1 ? shot : {
  ...shot,
  x: shot.x * k, y: shot.y * k, z: shot.z * k,
  tx: shot.tx * k, ty: shot.ty * k, tz: shot.tz * k,
});

/** 平面の座標 → ワールドの点（平面は y = 0。3 つ目の数は**高さ**＝浮かせたいとき） */
export const planeToWorld = ([u, v, lift = 0], plane) => ({ x: u - plane.w / 2, y: lift, z: v - plane.h / 2 });

const norm = (v) => { const l = Math.hypot(v[0], v[1], v[2]) || 1; return [v[0] / l, v[1] / l, v[2] / l]; };
const cross = (a, b) => [a[1] * b[2] - a[2] * b[1], a[2] * b[0] - a[0] * b[2], a[0] * b[1] - a[1] * b[0]];
const dot = (a, b) => a[0] * b[0] + a[1] * b[1] + a[2] * b[2];

/**
 * カメラの基底（右・上・後ろ）と焦点距離。**three の `lookAt` と同じ組み立て**
 * （`xc = up × zc`、`yc = zc × xc`）。ここが投影と逆投影の共通の土台（2 か所に同じ式を書かない）。
 */
function basisOf(shot, canvas) {
  const c = [shot.x, shot.y, shot.z];
  const up = shot.up ?? [0, 1, 0];
  const zc = norm([c[0] - shot.tx, c[1] - shot.ty, c[2] - shot.tz]);   // カメラの後ろ向き
  const xc = norm(cross(up, zc));
  const yc = cross(zc, xc);
  return {
    c, xc, yc, zc,
    // 縦の画角なので高さから。横は aspect（= 舞台の 幅 / 高さ）で決まる＝ three と同じ
    k: (canvas.h / 2) / Math.tan(((shot.fov ?? 45) * Math.PI) / 360),
  };
}

/**
 * 平面の座標 → 舞台のピクセルに写す関数（`(shot, plane, canvas)` ごとに 1 回作る）。
 * カメラの後ろの点は null。
 */
export function project(shot = LIVE_SHOT, plane, canvas = DEFAULT_CANVAS) {
  const { c, xc, yc, zc, k } = basisOf(shot, canvas);
  return (pt) => {
    const p = planeToWorld(pt, plane);
    const d = [p.x - c[0], p.y - c[1], p.z - c[2]];
    const depth = -dot(d, zc);
    if (!(depth > 1e-6)) return null;
    const s = k / depth;
    return [canvas.w / 2 + dot(d, xc) * s, canvas.h / 2 - dot(d, yc) * s];
  };
}

/**
 * 舞台のピクセル → 平面の座標（`project` の逆。**平面 y = 0 の上の点**）。
 * **平面と平行な光線は当たらない**ので、地平線の向こうは `[NaN, NaN]`
 * （「射影と違って常に解ける」わけではない）。
 */
export function unproject(shot = LIVE_SHOT, plane, canvas = DEFAULT_CANVAS) {
  const { c, xc, yc, zc, k } = basisOf(shot, canvas);
  return ([X, Y]) => {
    const a = (X - canvas.w / 2) / k; const b = (canvas.h / 2 - Y) / k;
    const dir = [a * xc[0] + b * yc[0] - zc[0], a * xc[1] + b * yc[1] - zc[1], a * xc[2] + b * yc[2] - zc[2]];
    if (!(Math.abs(dir[1]) > 1e-12)) return [NaN, NaN];
    const t = -c[1] / dir[1];
    if (!(t > 0)) return [NaN, NaN];
    return [c[0] + dir[0] * t + plane.w / 2, c[2] + dir[2] * t + plane.h / 2];
  };
}
